import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, PlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { ProductImage } from '../../../ui/ProductImage';
import { useCatalog } from '../../../contexts/CatalogContext';
import { useSignedInAccount } from '../../../contexts/AccountContext';
import { useSkinPlan } from '../../../hooks/useSkinPlan';
import { euro } from '../../../core/format/currency';
import { bumpCandidates, dayShort } from '../../../utils/account';
import { ease } from '../../../core/format/motion';
import { isSoldOut } from '../../../core/rules/stock';
import { btnAddRound } from '../../../ui/styles';

/** Zoveel suggesties; meer is kiezen in plaats van aanvullen. */
const LIMIT = 4;

/**
 * "Eenmalig erbij" — één tik, geen extra verzendkosten, geen verplichting.
 * Een lijst met het ronde plusje (zoals overal op de site), geen kaarten in een kaart.
 * Suggesties passen bij haar plan en zitten nog niet in het abonnement.
 */
export function OrderBump() {
  const {
    account,
    subscription
  } = useSignedInAccount();
  const {
    products: catalogProducts
  } = useCatalog();
  const {
    items
  } = useSkinPlan();
  const reduce = useReducedMotion();
  const sub = account.subscription;
  if (!sub || sub.status !== 'actief') return null;

  // Wat al eenmalig meegaat blijft zichtbaar (om weer weg te halen). Uitverkocht kan niet mee.
  const products = bumpCandidates(catalogProducts, account, items.map(i => i.productId)).filter(p => sub.extras.includes(p.id) || !isSoldOut(p)).slice(0, LIMIT);
  if (products.length === 0) return null;
  return <section id="order-bump" aria-labelledby="bump-title" className="min-w-0 scroll-mt-24" data-id="element-6235" data-mp="345f7hascg4zb">
      <h3 id="bump-title" className="text-[15px] text-ink" data-id="element-6236" data-mp="394rupwn4ne2f">
        Eenmalig erbij
      </h3>
      <p className="mt-0.5 text-[14px] text-muted" data-id="element-6237" data-mp="phrumbey4tl">Gaat mee op {dayShort(sub.nextDelivery)}, zonder extra verzendkosten. Alleen deze keer.</p>
      <ul className="mt-3 divide-y divide-line border-y border-line" aria-label="Suggesties voor je volgende levering" data-id="element-6238" data-mp="5hrg7xwvqwtr">
        {products.map(p => {
        const added = sub.extras.includes(p.id);
        return <li key={p.id} className="flex items-center gap-3 py-3 sm:gap-4" data-id="element-6239" data-mp="6bqq7lce9mya">
              <span className="h-14 w-12 shrink-0 bg-sand" aria-hidden="true" data-id="element-6240" data-mp="6dv48w40c2ca">
                <ProductImage src={p.image} alt="" className="h-full w-full p-1" data-id="element-6241" data-mp="1c5ox49r52ie" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-6242" data-mp="3skjo67q2j2pj">
                <span className="block text-[13px] text-muted" data-id="element-6243" data-mp="3nsi9yg9cb1wv">{p.brand}</span>
                <span className="block truncate text-[15px] text-ink" data-id="element-6244" data-mp="3nqfg1l52gomv">{p.name}</span>
              </span>
              <span className="shrink-0 text-[14px] tabular-nums text-muted" data-id="element-6245" data-mp="3erk8a4mdj6hg">{euro(p.price)}</span>
              <button type="button" aria-pressed={added} aria-label={added ? `${p.name} gaat mee; weer weghalen` : `${p.name} eenmalig meesturen, ${euro(p.price)}`} onClick={async () => {
            if ((await subscription({
              type: 'toggleExtra',
              productId: p.id
            })) && !added) toast.success('Gaat mee met je levering', {
              description: p.name
            });
          }} className={btnAddRound(added)} data-id="element-6246" data-mp="39shb16cmy4k1">
                <AnimatePresence mode="wait" initial={false} data-id="element-6247" data-mp="1yb4mx6jlpbos">
                  <motion.span key={added ? 'on' : 'off'} initial={reduce ? {
                opacity: 0
              } : {
                opacity: 0,
                rotate: -90,
                scale: 0.96
              }} animate={{
                opacity: 1,
                rotate: 0,
                scale: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.16,
                ease
              }} className="flex" data-id="element-6248" data-mp="1yal6xvdigxde">
                    {added ? <CheckIcon className="h-4 w-4" strokeWidth={2} aria-hidden="true" data-id="element-6249" data-mp="1fdjyu1onuh58" /> : <PlusIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6250" data-mp="itbc5x2kya99" />}
                  </motion.span>
                </AnimatePresence>
              </button>
            </li>;
      })}
      </ul>
    </section>;
}